import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { ParcelInput } from "types/ShippingTypes";

/** One parcel row as typed: kilograms, kept as text so the field can start
 *  empty (D24: the packer weighs the box, the estimate is only a hint). */
export type ParcelDraft = { weightKg: string };

export const emptyParcel = (): ParcelDraft => ({ weightKg: "" });

const parseKg = (value: string): number | null => {
  const number = Number(value.replace(",", ".").trim());
  return value.trim() !== "" && Number.isFinite(number) && number > 0
    ? number
    : null;
};

/** Grams per parcel, or null while any weight is missing or invalid. */
export const parcelsToInput = (
  parcels: ParcelDraft[]
): ParcelInput[] | null => {
  const weights = parcels.map((parcel) => parseKg(parcel.weightKg));
  if (weights.length === 0 || weights.some((weight) => weight === null)) {
    return null;
  }
  return weights.map((kg) => ({
    weightGrams: Math.round((kg as number) * 1000),
  }));
};

export const WEIGHT_WARN_HIGH = 2.0;
export const WEIGHT_WARN_LOW = 0.5;

type Props = {
  parcels: ParcelDraft[];
  onChange: (parcels: ParcelDraft[]) => void;
  /** Items plus the default box weight, from the backend. */
  estimateGrams: number | null;
  /** The order's paid band; null for a custom price (no band check). */
  paidToKg: number | null;
  disabled?: boolean;
};

const ParcelsForm: React.FC<Props> = ({
  parcels,
  onChange,
  estimateGrams,
  paidToKg,
  disabled,
}) => {
  const typed = parcels.map((parcel) => parseKg(parcel.weightKg));
  const complete = typed.every((weight) => weight !== null);
  const totalKg = typed.reduce<number>((sum, weight) => sum + (weight ?? 0), 0);
  const estimateKg = estimateGrams != null ? estimateGrams / 1000 : null;

  const farFromEstimate =
    complete &&
    estimateKg != null &&
    estimateKg > 0 &&
    (totalKg > estimateKg * WEIGHT_WARN_HIGH ||
      totalKg < estimateKg * WEIGHT_WARN_LOW);
  const overBand = complete && paidToKg != null && totalKg > paidToKg;

  const update = (index: number, weightKg: string) =>
    onChange(parcels.map((parcel, i) => (i === index ? { weightKg } : parcel)));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {parcels.map((parcel, index) => (
        <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            size="small"
            label={`Parcel ${index + 1} weight`}
            value={parcel.weightKg}
            onChange={(event) => update(index, event.target.value)}
            placeholder={
              estimateKg != null && index === 0
                ? `estimate ${estimateKg.toFixed(2)}`
                : ""
            }
            error={parcel.weightKg !== "" && typed[index] === null}
            disabled={disabled}
            inputProps={{ inputMode: "decimal" }}
            InputProps={{
              endAdornment: <InputAdornment position="end">kg</InputAdornment>,
            }}
            sx={{ width: 220 }}
          />
          {parcels.length > 1 && (
            <IconButton
              size="small"
              aria-label="Remove parcel"
              disabled={disabled}
              onClick={() => onChange(parcels.filter((_, i) => i !== index))}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ))}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={disabled}
          onClick={() => onChange([...parcels, emptyParcel()])}
        >
          Add parcel
        </Button>
        {estimateKg != null && (
          <Typography variant="body2" color="text.secondary">
            Estimate: {estimateKg.toFixed(2)} kg (items + box)
          </Typography>
        )}
        {complete && parcels.length > 1 && (
          <Typography variant="body2">
            Total: {totalKg.toFixed(2)} kg
          </Typography>
        )}
      </Box>
      {farFromEstimate && (
        <Alert severity="warning">
          The typed weight ({totalKg.toFixed(2)} kg) is far from the estimate (
          {estimateKg?.toFixed(2)} kg). Check the scale and the box.
        </Alert>
      )}
      {overBand && (
        <Alert severity="warning">
          Paid ≤ {paidToKg} kg, parcel is {totalKg.toFixed(2)} kg. You can still
          book; it shows as a label issue.
        </Alert>
      )}
    </Box>
  );
};

export default ParcelsForm;
